'use client'

import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Button, Card, Loader, Spacer } from '@vinted/web-ui'

import {
  businessAccountFromUserDto,
  setBusinessAccountPolicies,
} from '@marketplace-web/business-accounts/business-accounts-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import LegalImprint from './LegalImprint'
import PoliciesTextarea from './PoliciesTextarea'
import useFetchPoliciesSettingsData from './useFetchPoliciesSettingsData'

const TRANSLATE_PREFIX = 'policies_settings'

enum Field {
  TermsAndConditions = 'termsAndConditions',
  ReturnPolicy = 'returnPolicy',
  AdditionalInformation = 'additionalInformation',
}

type FormModel = {
  termsAndConditions: string
  returnPolicy: string
  additionalInformation: string
}

const PoliciesSettings = () => {
  const { user } = useSession()
  const businessAccountId = businessAccountFromUserDto(user)?.id as number

  const [policies, isLoading] = useFetchPoliciesSettingsData()
  const translate = useTranslate(TRANSLATE_PREFIX)

  const formMethods = useForm<FormModel>({
    defaultValues: {
      termsAndConditions: '',
      returnPolicy: '',
      additionalInformation: '',
    },
  })

  const { reset, handleSubmit, formState } = formMethods

  useEffect(() => {
    reset(policies)
  }, [reset, policies])

  if (!businessAccountId) return null

  const onSubmit = async ({
    termsAndConditions,
    returnPolicy,
    additionalInformation,
  }: FormModel) => {
    await setBusinessAccountPolicies({
      businessAccountId,
      termsAndConditions,
      returnPolicy,
      additionalInformation,
    })
  }

  if (isLoading) {
    return (
      <div className="u-flexbox u-justify-content-center">
        <Loader testId="policies-loader" />
      </div>
    )
  }

  return (
    <>
      <Spacer size="large" />
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card>
            <LegalImprint />
          </Card>
          <Spacer size="x-large" />
          <Card>
            <PoliciesTextarea
              name={Field.TermsAndConditions}
              title={translate('terms_and_conditions.title')}
              placeholder={translate('terms_and_conditions.placeholder')}
            />
          </Card>
          <Spacer size="x-large" />
          <Card>
            <PoliciesTextarea
              name={Field.ReturnPolicy}
              title={translate('return_policy.title')}
              placeholder={translate('return_policy.placeholder')}
            />
          </Card>
          <Spacer size="x-large" />
          <Card>
            <PoliciesTextarea
              name={Field.AdditionalInformation}
              title={translate('additional_information.title')}
              placeholder={translate('additional_information.placeholder')}
            />
          </Card>
          <Spacer size="large" />
          <div className="u-float-right">
            <Button
              type="submit"
              styling="filled"
              size="medium"
              disabled={formState.isSubmitting}
              isLoading={formState.isSubmitting}
              inline
              onClick={(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
                if (formState.isSubmitting) {
                  event.preventDefault()
                }
              }}
            >
              {translate('actions.submit')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  )
}

export default PoliciesSettings
